import type { W_WorkRequest_Create_RM } from 'ab-api/web/workRequest/create';
import type { W_WorkRequest_Edit_RM } from 'ab-api/web/workRequest/edit';
import type { W_WorkRequest_FindWorkRequestForEdit_VM } from 'ab-api/web/workRequest/findWorkRequestForEdit';

import type { WorkLocationAddressVM } from 'ab-viewModels/workRequest/workRequestDetailsUpsert.viewModel';

import type { ContactRM } from 'ab-requestModels/workRequest/jobUpsert.requestModel';

import { isNullOrUndefined } from '@acceligentllc/shared/utils/extensions';
import { toRawPhoneNumber } from '@acceligentllc/shared/utils/phone';
import { isValidTextInput } from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { AccountPermissionTemplate } from '@acceligentllc/shared/enums/accountPermissionTemplate';
import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { EmailTypes, PhoneTypes } from '@acceligentllc/shared/enums/contactMethodType';
import type { CountryCode } from '@acceligentllc/shared/enums/countryCode';
import type { DeliverableDataType } from '@acceligentllc/shared/enums/deliverableDataType';
import type { DirectoryIcon } from '@acceligentllc/shared/enums/directoryIcon';
import type { FileType } from '@acceligentllc/shared/enums/fileType';
import type { Priority } from '@acceligentllc/shared/enums/priority';
import type { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import type { WorkRequestBidStatus } from '@acceligentllc/shared/enums/workRequestBidStatus';
import type { WorkRequestDisposalMethod } from '@acceligentllc/shared/enums/workRequestDisposalMethod';
import type { WorkRequestJobHazardAssessmentStatus } from '@acceligentllc/shared/enums/workRequestJobHazardAssessmentStatus';

import { EMAIL_REGEX } from '@acceligentllc/shared/constants/regex';

import { MAX_WR_TRUCK_EQUIPMENT_ADDITIONAL_FIELDS } from 'ab-common/constants/value';

class WorkRequestStatusFM {
	id: number;
	name: string;
	description: Nullable<string>;
	color: ColorPalette;
}

class EmployeeOptionFM {
	id: number;
	formattedCode: string;
	firstName: string;
	lastName: string;
	uniqueId: string;
	accountTemplate: AccountPermissionTemplate;
}

class AddressFM {
	id: number;
	street: string;
	streetNumber: Nullable<string>;
	route: Nullable<string>;
	suite: Nullable<string>;
	city: Nullable<string>;
	state: Nullable<string>;
	aa2: Nullable<string>;
	aa3: Nullable<string>;
	zip: Nullable<string>;
	postalOfficeBoxCode: Nullable<string>;
	country: Nullable<string>;
	latitude: number;
	longitude: number;
}

interface WorkLocationAddressBackupFM {
	aa1: Nullable<string>;
	aa2: Nullable<string>;
	aa3: Nullable<string>;
	country: Nullable<string>;
	latitude: Nullable<number>;
	longitude: Nullable<number>;
	locality: Nullable<string>;
	postalCode: Nullable<string>;
	street: Nullable<string>;
	streetNumber: Nullable<string>;
	suite: Nullable<string>;
	postalOfficeBoxCode: Nullable<string>;
	neighborhood: Nullable<string>;
	formattedAddress: Nullable<string>;
}

export class WorkLocationAddressFM {
	id: number;
	street: Nullable<string>;
	streetNumber: Nullable<string>;
	postalCode: Nullable<string>;
	neighborhood: Nullable<string>;
	city: Nullable<string>;
	state: Nullable<string>;
	country: Nullable<string>;
	notes: Nullable<string>;
	location: Nullable<string>;
	backup: Nullable<WorkLocationAddressBackupFM>;

	public static fromVM = (vm: WorkLocationAddressVM): WorkLocationAddressFM => {
		return {
			id: vm.id,
			street: vm.street,
			streetNumber: vm.streetNumber,
			postalCode: vm.postalCode,
			neighborhood: vm.neighborhood,
			city: vm.city,
			state: vm.state,
			country: vm.country,
			notes: vm.notes,
			location: vm.location,
			backup: {
				latitude: vm.latitude,
				longitude: vm.longitude,
				aa1: null,
				aa2: null,
				aa3: null,
				country: null,
				locality: null,
				postalCode: null,
				street: null,
				streetNumber: null,
				suite: null,
				postalOfficeBoxCode: null,
				neighborhood: null,
				formattedAddress: null,
			},
		};
	};
}

class ContactAddressFM {
	id: number;
	contactId: number;
	addressId: number;
	address: AddressFM;
}

class ContactMethodFM {
	id: number;
	contactId: number;
	type: EmailTypes | PhoneTypes;
	value: string;
	countryCode: Nullable<CountryCode>;
	extension: Nullable<string>;
}

class DeliverableDataFM {
	id: number;
	name: string;
	type: DeliverableDataType;
}

interface ContactFM {
	id: number;
	workOrderContactId: number;
	role: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;

	addresses: ContactAddressFM[];
	emails: ContactMethodFM[];
	phones: ContactMethodFM[];

	contactAddressIds: number[];
	contactEmailIds: number[];
	contactPhoneIds: number[];
}

class OfficeFM {
	id: number;
	nickname: string;
}

class DivisionFM {
	id: number;
	name: string;
}

class AccountFM {
	id: number;
	firstName: string;
	lastName: string;
	uniqueId: string;
}

class SalesFM {
	id: number;
	fullName: string;
}

class EmployeeFM {
	id: number;
	firstName: string;
	lastName: string;
}

interface UserInfoFM {
	accountId?: Nullable<number>;
	userId?: number;
	firstName?: string;
	lastName?: string;
	fullName: string;
}

class WorkRequestDirectoryFM {
	id: number;
	name: string;
	parentId: Nullable<number>;
	icon: Nullable<DirectoryIcon>;
	lastModifiedAt: Nullable<Date>;
	lastModifiedBy: Nullable<UserInfoFM>;
	attachmentIds: number[];
}

export type AdditionalTrucksEquipmentField = {
	name: string;
	isChecked: boolean;
};

interface AttachmentFM {
	id: number;
	size: number;
	name: string;
	type: FileType;
	status: ResourceStatus;
	storageName: string;
	storageContainer: string;
	/** original version with presigned url */
	originalSrc: string;
	lastModifiedAt: Date;
	uploadedBy: Nullable<UserInfoFM>;
}

class WorkRequestUpsertFM {

	bidStatus: WorkRequestBidStatus;
	lostForReason: Nullable<string>;
	convertedToJobId: Nullable<number>;

	// Sales
	salesId: Nullable<string>;
	salesLead: Nullable<SalesFM>;
	salesLeadId: Nullable<number>;
	estimateTotal: number;

	// Job Summary
	/** on form shown as Work Request Id */
	jobCode: string;
	title: string;
	isInternal: boolean;
	customerCompanyName: Nullable<string>;
	customerReferencePO: Nullable<string>;
	isEmergencyJob: boolean;
	priority: Priority;
	jobStatusId: Nullable<number>;
	jobStatus: Nullable<WorkRequestStatusFM>;
	projectManagerId: Nullable<number>;
	projectManager: Nullable<EmployeeFM>;
	officeId: Nullable<number>;
	office: Nullable<OfficeFM>;
	divisionId: Nullable<number>;
	division: Nullable<DivisionFM>;

	// Job Detail
	projectOwner: Nullable<string>;
	ownerReference: Nullable<string>;
	taxExempt: boolean;
	generalContractor: Nullable<string>;
	contractorReference: Nullable<string>;
	isUnion: boolean;
	isPrevailingWage: boolean;
	jurisdiction: Nullable<string>;

	// Requirements
	requestMBEOrWBERequirementsNote: Nullable<string>;
	requestBondRequirementsNote: Nullable<string>;
	requestHSERequirementsNote: Nullable<string>;
	retainage: Nullable<string>;
	requestInsuranceRequirementsNote: Nullable<string>;
	requestLiquidatedDamagesNote: Nullable<string>;

	// Schedule Info
	targetStartDate: Nullable<string>;
	targetCompletionDate: Nullable<string>;
	targetDaysFromStart: Nullable<number>;
	startDate: Nullable<string>;
	guaranteedCompletionDate: Nullable<string>;
	guaranteedDaysFromStart: Nullable<number>;

	// Contacts
	customerContact: Nullable<ContactFM>;
	siteContact: Nullable<ContactFM>;
	billingContact: Nullable<ContactFM>;
	billTo: Nullable<string>;
	billToAddress: Nullable<string>;
	billToCityStateZip: Nullable<string>;
	billToPhoneNumber: Nullable<string>;
	billToFaxNumber: Nullable<string>;
	billToContactName: Nullable<string>;
	billToEmail: Nullable<string>;
	additionalContacts: Nullable<ContactFM[]>;

	// Deliverables
	isDeliverable: boolean;
	deliverableAssigneeId: Nullable<number>;
	deliverableAssignee: Nullable<AccountFM>;
	deliverableSoftwareId: Nullable<number>;
	deliverableSoftware: Nullable<DeliverableDataFM>;
	deliverableFileFormatId: Nullable<number>;
	deliverableFileFormat: Nullable<DeliverableDataFM>;
	deliverableCodeStandardId: Nullable<number>;
	deliverableCodeStandard: Nullable<DeliverableDataFM>;
	deliverableDeliveryMethodId: Nullable<number>;
	deliverableDeliveryMethod: Nullable<DeliverableDataFM>;
	deliverableDeliveryTimelineId: Nullable<number>;
	deliverableDeliveryTimeline: Nullable<DeliverableDataFM>;
	deliverableNotes: Nullable<string>;

	// Work Location
	workLocation: Nullable<WorkLocationAddressFM>;

	// Summary of Work
	requestJobNote: Nullable<string>;

	// Survey data
	surveyNeeded: boolean;
	surveyedById: Nullable<number>;
	surveyedBy: Nullable<EmployeeOptionFM>;
	surveyDate: Nullable<string>;
	waterAvailable: boolean;
	waterSupplyInformation: Nullable<string>;
	subcontractorNeeded: Nullable<string>;
	subcontractorInformation: Nullable<string>;
	pipeSizes: Nullable<string>;
	pipeTypes: Nullable<string>;
	manholeDepths: Nullable<string>;
	manholeFrameDiameters: Nullable<string>;
	informationsProvidedByCustomersBy: Nullable<string>;

	// Trucks / Equiment
	jc80gpm: boolean;
	sjc80gpm: boolean;
	jcRecycler80: boolean;
	jjc120gpm: boolean;
	jjc160gpm: boolean;
	jcHiRail: boolean;
	jetter: boolean;
	harbenJetter: boolean;
	hjJetter: boolean;
	tvTruck: boolean;
	tvLateralTk: boolean;
	tvGroutTk: boolean;
	tvLatGroutTk: boolean;
	tvSonarTk: boolean;
	tvPolaris: boolean;
	tvLCRTk: boolean;
	tvLateralReinstator: boolean;
	tvMTHTk: boolean;
	tvLaserTk: boolean;
	minicam: boolean;
	vacTk: boolean;
	hivacTk: boolean;
	hiRailvacTk: boolean;
	tanker: boolean;
	wb: boolean;
	hwBoiler: boolean;
	pressureWash: boolean;
	airComp: boolean;
	clamTk: boolean;
	bucketMach: boolean;
	mhRehabTk: boolean;
	microtrax: boolean;
	smallEasementMachine: boolean;
	bigEasementMachine: boolean;
	arrowBoard: boolean;
	supportTk: boolean;
	rollOffTk: boolean;
	pump: boolean;
	pigLaunch: boolean;
	stoveMach: boolean;
	powerRodder: boolean;
	reeferTruck: boolean;
	lateralReinstatorTrailer: boolean;
	steamCureTruck: boolean;
	airTesting: boolean;
	trackMachine: boolean;
	additionalTruckEquipmentFields: AdditionalTrucksEquipmentField[];
	numberOfEmployees: number;

	// Disposal
	ableToCleanAndWashEquipmentAtCustomerLocation: boolean;
	cleanAndWashLocation: Nullable<string>;
	isWasteGeneratedSolid: boolean;
	isWasteGeneratedSludge: boolean;
	isWasteGeneratedLiquid: boolean;
	isWasteHazardous: boolean;
	isWasteManifest: boolean;
	wasteDescription: Nullable<string>;
	expectedWasteQuantity: Nullable<number>;
	disposalMethod: Nullable<WorkRequestDisposalMethod>;
	disposalInstructions: Nullable<string>;
	disposalContact: Nullable<ContactFM>;

	// Job Hazard Summary
	hazardSummary: Nullable<string>;
	permitsRequired: Nullable<string[]>;

	// Safety
	environmentalExposure: boolean;
	environmentalExposureDescription: Nullable<string>;
	respiratoryProtection: boolean;
	respiratoryProtectionDescription: Nullable<string>;
	overheadHazards: boolean;
	overheadHazardsDescription: Nullable<string>;
	heightWork: boolean;
	heightWorkDescription: Nullable<string>;
	mechanicalHazards: boolean;
	mechanicalHazardsDescription: Nullable<string>;
	electricalHazards: boolean;
	electricalHazardsDescription: Nullable<string>;
	hazardousMaterials: boolean;
	hazardousMaterialsDescription: Nullable<string>;
	dangerousAnimalsOrPlants: boolean;
	dangerousAnimalsOrPlantsDescription: Nullable<string>;

	// Environmental Protection
	waterOrWetlands: boolean;
	waterOrWetlandsDescription: Nullable<string>;
	stormDrain: boolean;
	stormDrainDescription: Nullable<string>;
	bypassPumping: boolean;
	bypassPumpingDescription: Nullable<string>;
	sensitiveEnvironment: boolean;
	sensitiveEnvironmentDescription: Nullable<string>;

	// Public Safety
	vehicleTraffic: boolean;
	vehicleTrafficDescription: Nullable<string>;
	pedestrianCrossing: boolean;
	pedestrianCrossingDescription: Nullable<string>;

	// Status
	jobHazardAssessmentStatus: Nullable<WorkRequestJobHazardAssessmentStatus>;

	directories: WorkRequestDirectoryFM[];
	workRequestAttachments: AttachmentFM[];
	uploadedAttachmentIds?: number[];
	uploadingAttachments?: Record<string, true>;

	constructor(vm: W_WorkRequest_FindWorkRequestForEdit_VM) {
		this.bidStatus = vm.bidStatus;
		this.lostForReason = vm.lostForReason;
		this.salesId = vm.salesId;
		this.salesLead = vm.salesLead;
		this.salesLeadId = vm.salesLeadId;
		this.estimateTotal = vm.estimateTotal;
		this.jobCode = vm.jobCode;
		this.title = vm.title;
		this.isInternal = vm.isInternal;
		this.customerCompanyName = vm.customerCompanyName;
		this.customerReferencePO = vm.customerReferencePO;
		this.isEmergencyJob = vm.isEmergencyJob;
		this.priority = vm.priority;
		this.jobStatusId = vm.jobStatusId;
		this.jobStatus = vm.jobStatus;
		this.projectManagerId = vm.projectManagerId;
		this.projectManager = vm.projectManager;
		this.officeId = vm.officeId;
		this.office = vm.office;
		this.divisionId = vm.divisionId;
		this.division = vm.division;
		this.projectOwner = vm.projectOwner;
		this.ownerReference = vm.ownerReference;
		this.taxExempt = vm.taxExempt;
		this.generalContractor = vm.generalContractor;
		this.contractorReference = vm.contractorReference;
		this.isUnion = vm.isUnion;
		this.isPrevailingWage = vm.isPrevailingWage;
		this.jurisdiction = vm.jurisdiction;
		this.requestMBEOrWBERequirementsNote = vm.requestMBEOrWBERequirementsNote;
		this.requestBondRequirementsNote = vm.requestBondRequirementsNote;
		this.requestHSERequirementsNote = vm.requestHSERequirementsNote;
		this.retainage = vm.retainage;
		this.requestInsuranceRequirementsNote = vm.requestInsuranceRequirementsNote;
		this.requestLiquidatedDamagesNote = vm.requestLiquidatedDamagesNote;
		this.targetStartDate = vm.targetStartDate;
		this.targetCompletionDate = vm.targetCompletionDate;
		this.targetDaysFromStart = vm.targetDaysFromStart;
		this.startDate = vm.startDate;
		this.guaranteedCompletionDate = vm.guaranteedCompletionDate;
		this.guaranteedDaysFromStart = vm.guaranteedDaysFromStart;
		this.customerContact = vm.customerContact;
		this.siteContact = vm.siteContact;
		this.billingContact = vm.billingContact;
		this.billTo = vm.billTo;
		this.billToAddress = vm.billToAddress;
		this.billToCityStateZip = vm.billToCityStateZip;
		this.billToPhoneNumber = vm.billToPhoneNumber;
		this.billToFaxNumber = vm.billToFaxNumber;
		this.billToContactName = vm.billToContactName;
		this.billToEmail = vm.billToEmail;
		this.additionalContacts = vm.additionalContacts;
		this.isDeliverable = vm.isDeliverable;
		this.deliverableAssigneeId = vm.deliverableAssigneeId;
		this.deliverableAssignee = vm.deliverableAssignee;
		this.deliverableSoftwareId = vm.deliverableSoftwareId;
		this.deliverableSoftware = vm.deliverableSoftware;
		this.deliverableFileFormatId = vm.deliverableFileFormatId;
		this.deliverableFileFormat = vm.deliverableFileFormat;
		this.deliverableCodeStandardId = vm.deliverableCodeStandardId;
		this.deliverableCodeStandard = vm.deliverableCodeStandard;
		this.deliverableDeliveryMethodId = vm.deliverableDeliveryMethodId;
		this.deliverableDeliveryMethod = vm.deliverableDeliveryMethod;
		this.deliverableDeliveryTimelineId = vm.deliverableDeliveryTimelineId;
		this.deliverableDeliveryTimeline = vm.deliverableDeliveryTimeline;
		this.deliverableNotes = vm.deliverableNotes;
		this.workLocation = vm.workLocation ? WorkLocationAddressFM.fromVM(vm.workLocation) : null;
		this.requestJobNote = vm.requestJobNote;
		this.surveyNeeded = vm.surveyNeeded;
		this.surveyedById = vm.surveyedById;
		this.surveyedBy = vm.surveyedBy;
		this.surveyDate = vm.surveyDate;
		this.waterAvailable = vm.waterAvailable;
		this.waterSupplyInformation = vm.waterSupplyInformation;
		this.subcontractorNeeded = vm.subcontractorNeeded;
		this.subcontractorInformation = vm.subcontractorInformation;
		this.pipeSizes = vm.pipeSizes;
		this.pipeTypes = vm.pipeTypes;
		this.manholeDepths = vm.manholeDepths;
		this.manholeFrameDiameters = vm.manholeFrameDiameters;
		this.informationsProvidedByCustomersBy = vm.informationsProvidedByCustomersBy;
		this.jc80gpm = vm.jc80gpm;
		this.sjc80gpm = vm.sjc80gpm;
		this.jcRecycler80 = vm.jcRecycler80;
		this.jjc120gpm = vm.jjc120gpm;
		this.jjc160gpm = vm.jjc160gpm;
		this.jcHiRail = vm.jcHiRail;
		this.jetter = vm.jetter;
		this.harbenJetter = vm.harbenJetter;
		this.hjJetter = vm.hjJetter;
		this.tvTruck = vm.tvTruck;
		this.tvLateralTk = vm.tvLateralTk;
		this.tvGroutTk = vm.tvGroutTk;
		this.tvLatGroutTk = vm.tvLatGroutTk;
		this.tvSonarTk = vm.tvSonarTk;
		this.tvPolaris = vm.tvPolaris;
		this.tvLCRTk = vm.tvLCRTk;
		this.tvLateralReinstator = vm.tvLateralReinstator;
		this.tvMTHTk = vm.tvMTHTk;
		this.tvLaserTk = vm.tvLaserTk;
		this.minicam = vm.minicam;
		this.vacTk = vm.vacTk;
		this.hivacTk = vm.hivacTk;
		this.hiRailvacTk = vm.hiRailvacTk;
		this.tanker = vm.tanker;
		this.wb = vm.wb;
		this.hwBoiler = vm.hwBoiler;
		this.pressureWash = vm.pressureWash;
		this.airComp = vm.airComp;
		this.clamTk = vm.clamTk;
		this.bucketMach = vm.bucketMach;
		this.mhRehabTk = vm.mhRehabTk;
		this.microtrax = vm.microtrax;
		this.smallEasementMachine = vm.smallEasementMachine;
		this.bigEasementMachine = vm.bigEasementMachine;
		this.arrowBoard = vm.arrowBoard;
		this.supportTk = vm.supportTk;
		this.rollOffTk = vm.rollOffTk;
		this.pump = vm.pump;
		this.pigLaunch = vm.pigLaunch;
		this.stoveMach = vm.stoveMach;
		this.powerRodder = vm.powerRodder;
		this.reeferTruck = vm.reeferTruck;
		this.lateralReinstatorTrailer = vm.lateralReinstatorTrailer;
		this.steamCureTruck = vm.steamCureTruck;
		this.airTesting = vm.airTesting;
		this.trackMachine = vm.trackMachine;
		this.numberOfEmployees = vm.numberOfEmployees;
		this.additionalTruckEquipmentFields = vm.additionalTruckEquipmentFields;
		this.ableToCleanAndWashEquipmentAtCustomerLocation = vm.ableToCleanAndWashEquipmentAtCustomerLocation;
		this.cleanAndWashLocation = vm.cleanAndWashLocation;
		this.isWasteGeneratedSolid = vm.isWasteGeneratedSolid;
		this.isWasteGeneratedSludge = vm.isWasteGeneratedSludge;
		this.isWasteGeneratedLiquid = vm.isWasteGeneratedLiquid;
		this.isWasteHazardous = vm.isWasteHazardous;
		this.isWasteManifest = vm.isWasteManifest;
		this.wasteDescription = vm.wasteDescription;
		this.expectedWasteQuantity = vm.expectedWasteQuantity;
		this.disposalMethod = vm.disposalMethod;
		this.disposalInstructions = vm.disposalInstructions;
		this.disposalContact = vm.disposalContact;
		this.hazardSummary = vm.hazardSummary;
		this.permitsRequired = vm.permitsRequired;
		this.environmentalExposure = vm.environmentalExposure;
		this.environmentalExposureDescription = vm.environmentalExposureDescription;
		this.respiratoryProtection = vm.respiratoryProtection;
		this.respiratoryProtectionDescription = vm.respiratoryProtectionDescription;
		this.overheadHazards = vm.overheadHazards;
		this.overheadHazardsDescription = vm.overheadHazardsDescription;
		this.heightWork = vm.heightWork;
		this.heightWorkDescription = vm.heightWorkDescription;
		this.mechanicalHazards = vm.mechanicalHazards;
		this.mechanicalHazardsDescription = vm.mechanicalHazardsDescription;
		this.electricalHazards = vm.electricalHazards;
		this.electricalHazardsDescription = vm.electricalHazardsDescription;
		this.hazardousMaterials = vm.hazardousMaterials;
		this.hazardousMaterialsDescription = vm.hazardousMaterialsDescription;
		this.dangerousAnimalsOrPlants = vm.dangerousAnimalsOrPlants;
		this.dangerousAnimalsOrPlantsDescription = vm.dangerousAnimalsOrPlantsDescription;
		this.waterOrWetlands = vm.waterOrWetlands;
		this.waterOrWetlandsDescription = vm.waterOrWetlandsDescription;
		this.stormDrain = vm.stormDrain;
		this.stormDrainDescription = vm.stormDrainDescription;
		this.bypassPumping = vm.bypassPumping;
		this.bypassPumpingDescription = vm.bypassPumpingDescription;
		this.sensitiveEnvironment = vm.sensitiveEnvironment;
		this.sensitiveEnvironmentDescription = vm.sensitiveEnvironmentDescription;
		this.vehicleTraffic = vm.vehicleTraffic;
		this.vehicleTrafficDescription = vm.vehicleTrafficDescription;
		this.pedestrianCrossing = vm.pedestrianCrossing;
		this.pedestrianCrossingDescription = vm.pedestrianCrossingDescription;
		this.jobHazardAssessmentStatus = vm.jobHazardAssessmentStatus;
		this.directories = vm.directories;
		this.workRequestAttachments = vm.workRequestAttachments;
	}

	static getAttributeName = (name: keyof WorkRequestUpsertFM) => name;

	static validate = (form: WorkRequestUpsertFM): ValidationErrors => {
		const errors: ValidationErrors = {};

		if (!isValidTextInput(form.jobCode)) {
			errors.jobCode = 'Work Request ID is Required.';
		}

		if (!isValidTextInput(form.title)) {
			errors.title = 'Job Title is Required.';
		}

		if (!isValidTextInput(form.customerCompanyName)) {
			errors.customerCompanyName = 'Customer Company is Required.';
		}

		if (!form.priority) {
			errors.priority = 'Priority is Required.';
		}

		if (!form.officeId) {
			errors.officeId = 'Office is Required.';
		}

		if (!form.jobHazardAssessmentStatus) {
			errors.jobHazardAssessmentStatus = 'Job Hazard Assessment Status is Required.';
		}

		if (form.billToEmail && !EMAIL_REGEX.test(form.billToEmail)) {
			errors.billToEmail = 'Invalid email address';
		}

		if (form.workLocation && (!form.workLocation?.notes && (!form.workLocation?.street && !form.workLocation?.city && !form.workLocation?.neighborhood))) {
			errors.street = 'Work Location street or notes are required';
		}

		return errors;
	};

	static warn = (form: WorkRequestUpsertFM) => {
		const warnings: ValidationErrors = {};

		if (
			form?.workLocation &&
			(isNullOrUndefined(form.workLocation.backup?.latitude) || isNullOrUndefined(form.workLocation.backup?.longitude))
		) {
			warnings.workLocation = { location: 'Work location not recognized by Google Maps.' };
		}

		return warnings;
	};

	static normalizeNumberOfEmployees = (_value) => {
		if (+_value < 0) {
			return 0;
		}

		// Float doesnt make sense so just return floor
		if (!Number.isInteger(+_value)) {
			return Math.floor(_value);
		}

		return _value;
	};

	static toCreateRM = (form: WorkRequestUpsertFM): W_WorkRequest_Create_RM => {
		let workLocation = form.workLocation ?? null;
		if (!form.workLocation?.street && !form.workLocation?.notes && !form.workLocation?.city && !form.workLocation?.location) {
			workLocation = null;
		}

		return {
			requestJobNote: form.requestJobNote,
			jobHazardAssessmentStatus: form.jobHazardAssessmentStatus,
			// Sales
			salesId: form.salesId,
			salesLeadId: form.salesLeadId,
			estimateTotal: form.estimateTotal,
			// Job Summary
			jobCode: form.jobCode,
			title: form.title,
			isInternal: form.isInternal,
			customerCompanyName: form.customerCompanyName,
			customerReferencePO: form.customerReferencePO,
			isEmergencyJob: form.isEmergencyJob,
			priority: form.priority,
			jobStatusId: form.jobStatusId,
			projectManagerId: form.projectManagerId,
			officeId: form.officeId,
			divisionId: form.divisionId,
			// Job Detail
			projectOwner: form.projectOwner,
			ownerReference: form.ownerReference,
			taxExempt: form.taxExempt,
			generalContractor: form.generalContractor,
			contractorReference: form.contractorReference,
			isUnion: form.isUnion,
			isPrevailingWage: form.isPrevailingWage,
			jurisdiction: form.jurisdiction,
			// Requirements
			requestMBEOrWBERequirementsNote: form.requestMBEOrWBERequirementsNote,
			requestBondRequirementsNote: form.requestBondRequirementsNote,
			requestHSERequirementsNote: form.requestHSERequirementsNote,
			retainage: form.retainage,
			requestInsuranceRequirementsNote: form.requestInsuranceRequirementsNote,
			requestLiquidatedDamagesNote: form.requestLiquidatedDamagesNote,
			// Schedule Info
			targetStartDate: form.targetStartDate
				? TimeUtils.formatDate(form.targetStartDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			targetCompletionDate: form.targetCompletionDate
				? TimeUtils.formatDate(form.targetCompletionDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			startDate: form.startDate
				? TimeUtils.formatDate(form.startDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			guaranteedCompletionDate: form.guaranteedCompletionDate
				? TimeUtils.formatDate(form.guaranteedCompletionDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			// Contacts
			customerContact: form.customerContact,
			siteContact: form.siteContact,
			billingContact: form.billingContact,
			additionalContacts: form.additionalContacts,
			// Bill to
			billTo: form.billTo,
			billToAddress: form.billToAddress,
			billToCityStateZip: form.billToCityStateZip,
			billToPhoneNumber: form.billToPhoneNumber ? toRawPhoneNumber(form.billToPhoneNumber) : null,
			billToFaxNumber: form.billToFaxNumber,
			billToContactName: form.billToContactName,
			billToEmail: form.billToEmail,
			// Deliverables
			isDeliverable: form.isDeliverable,
			deliverableAssigneeId: form.deliverableAssigneeId,
			deliverableSoftwareId: form.deliverableSoftwareId,
			deliverableFileFormatId: form.deliverableFileFormatId,
			deliverableCodeStandardId: form.deliverableCodeStandardId,
			deliverableDeliveryMethodId: form.deliverableDeliveryMethodId,
			deliverableDeliveryTimelineId: form.deliverableDeliveryTimelineId,
			deliverableNotes: form.deliverableNotes,
			// Work Location
			workLocation,
			// Survey data
			surveyNeeded: form.surveyNeeded,
			surveyedById: form.surveyedById,
			surveyDate: form.surveyDate
				? TimeUtils.formatDate(form.surveyDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY)
				: null,
			waterAvailable: form.waterAvailable,
			waterSupplyInformation: form.waterSupplyInformation,
			subcontractorNeeded: form.subcontractorNeeded,
			subcontractorInformation: form.subcontractorInformation,
			pipeSizes: form.pipeSizes,
			pipeTypes: form.pipeTypes,
			manholeDepths: form.manholeDepths,
			manholeFrameDiameters: form.manholeFrameDiameters,
			informationsProvidedByCustomersBy: form.informationsProvidedByCustomersBy,
			jc80gpm: form.jc80gpm,
			sjc80gpm: form.sjc80gpm,
			jcRecycler80: form.jcRecycler80,
			jjc120gpm: form.jjc120gpm,
			jjc160gpm: form.jjc160gpm,
			jcHiRail: form.jcHiRail,
			jetter: form.jetter,
			harbenJetter: form.harbenJetter,
			hjJetter: form.hjJetter,
			tvTruck: form.tvTruck,
			tvLateralTk: form.tvLateralTk,
			tvGroutTk: form.tvGroutTk,
			tvLatGroutTk: form.tvLatGroutTk,
			tvSonarTk: form.tvSonarTk,
			tvPolaris: form.tvPolaris,
			tvLCRTk: form.tvLCRTk,
			tvLateralReinstator: form.tvLateralReinstator,
			tvMTHTk: form.tvMTHTk,
			tvLaserTk: form.tvLaserTk,
			minicam: form.minicam,
			vacTk: form.vacTk,
			hivacTk: form.hivacTk,
			hiRailvacTk: form.hiRailvacTk,
			tanker: form.tanker,
			wb: form.wb,
			hwBoiler: form.hwBoiler,
			pressureWash: form.pressureWash,
			airComp: form.airComp,
			clamTk: form.clamTk,
			bucketMach: form.bucketMach,
			mhRehabTk: form.mhRehabTk,
			microtrax: form.microtrax,
			smallEasementMachine: form.smallEasementMachine,
			bigEasementMachine: form.bigEasementMachine,
			arrowBoard: form.arrowBoard,
			supportTk: form.supportTk,
			rollOffTk: form.rollOffTk,
			pump: form.pump,
			pigLaunch: form.pigLaunch,
			stoveMach: form.stoveMach,
			powerRodder: form.powerRodder,
			reeferTruck: form.reeferTruck,
			lateralReinstatorTrailer: form.lateralReinstatorTrailer,
			steamCureTruck: form.steamCureTruck,
			airTesting: form.airTesting,
			trackMachine: form.trackMachine,
			additionalTruckEquipmentField1Name: form.additionalTruckEquipmentFields?.[0]?.name ?? null,
			additionalTruckEquipmentField1Checked: form.additionalTruckEquipmentFields?.[0]?.isChecked ?? null,
			additionalTruckEquipmentField2Name: form.additionalTruckEquipmentFields?.[1]?.name ?? null,
			additionalTruckEquipmentField2Checked: form.additionalTruckEquipmentFields?.[1]?.isChecked ?? null,
			numberOfEmployees: form.numberOfEmployees,
			// Disposal
			ableToCleanAndWashEquipmentAtCustomerLocation: form.ableToCleanAndWashEquipmentAtCustomerLocation,
			cleanAndWashLocation: form.cleanAndWashLocation,
			isWasteGeneratedSolid: form.isWasteGeneratedSolid,
			isWasteGeneratedSludge: form.isWasteGeneratedSludge,
			isWasteGeneratedLiquid: form.isWasteGeneratedLiquid,
			isWasteHazardous: form.isWasteHazardous,
			isWasteManifest: form.isWasteManifest,
			wasteDescription: form.wasteDescription,
			expectedWasteQuantity: form.expectedWasteQuantity,
			disposalMethod: form.disposalMethod,
			disposalInstructions: form.disposalInstructions,
			disposalContact: form.disposalContact,
			// Job Hazard Summary
			hazardSummary: form.hazardSummary,
			permitsRequired: form.permitsRequired,
			// Safety
			environmentalExposure: form.environmentalExposure,
			environmentalExposureDescription: form.environmentalExposureDescription,
			respiratoryProtection: form.respiratoryProtection,
			respiratoryProtectionDescription: form.respiratoryProtectionDescription,
			overheadHazards: form.overheadHazards,
			overheadHazardsDescription: form.overheadHazardsDescription,
			heightWork: form.heightWork,
			heightWorkDescription: form.heightWorkDescription,
			mechanicalHazards: form.mechanicalHazards,
			mechanicalHazardsDescription: form.mechanicalHazardsDescription,
			electricalHazards: form.electricalHazards,
			electricalHazardsDescription: form.electricalHazardsDescription,
			hazardousMaterials: form.hazardousMaterials,
			hazardousMaterialsDescription: form.hazardousMaterialsDescription,
			dangerousAnimalsOrPlants: form.dangerousAnimalsOrPlants,
			dangerousAnimalsOrPlantsDescription: form.dangerousAnimalsOrPlantsDescription,
			// Environmental Protection
			waterOrWetlands: form.waterOrWetlands,
			waterOrWetlandsDescription: form.waterOrWetlandsDescription,
			stormDrain: form.stormDrain,
			stormDrainDescription: form.stormDrainDescription,
			bypassPumping: form.bypassPumping,
			bypassPumpingDescription: form.bypassPumpingDescription,
			sensitiveEnvironment: form.sensitiveEnvironment,
			sensitiveEnvironmentDescription: form.sensitiveEnvironmentDescription,
			// Public Safety
			vehicleTraffic: form.vehicleTraffic,
			vehicleTrafficDescription: form.vehicleTrafficDescription,
			pedestrianCrossing: form.pedestrianCrossing,
			pedestrianCrossingDescription: form.pedestrianCrossingDescription,
			// Attachments
			directories: form.directories,

			uploadedAttachmentIds: form.uploadedAttachmentIds ?? [],
		};
	};

	static toEditRM = (form: Partial<WorkRequestUpsertFM>): Partial<W_WorkRequest_Edit_RM> => {
		const rm: Partial<W_WorkRequest_Edit_RM> = {};

		for (const _key of Object.keys(form) as (keyof WorkRequestUpsertFM)[]) {
			switch (_key) {
				// Skip VM properties that we only need ids from or dont need at all in RM
				case 'office':
				case 'salesLead':
				case 'projectManager':
				case 'office':
				case 'division':
				case 'deliverableSoftware':
				case 'deliverableAssignee':
				case 'deliverableDeliveryMethod':
				case 'deliverableCodeStandard':
				case 'deliverableDeliveryTimeline':
				case 'deliverableFileFormat':
				case 'jobStatus':
				case 'guaranteedDaysFromStart':
				case 'targetDaysFromStart':
				case 'workRequestAttachments':
				case 'uploadingAttachments':
					break;

				case 'surveyDate':
				case 'startDate':
				case 'targetStartDate':
				case 'targetCompletionDate':
				case 'guaranteedCompletionDate':
					rm[_key] = form[_key] ? TimeUtils.formatDate(form[_key], TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY) : null;
					break;

				case 'additionalContacts':
					rm[_key] = form[_key]?.reduce((_acc, _formAC) => {
						if (_formAC) {
							_acc.push({
								contactAddressIds: _formAC.contactAddressIds,
								contactEmailIds: _formAC.contactEmailIds,
								contactPhoneIds: _formAC.contactPhoneIds,
								id: _formAC.id,
								role: _formAC.role,
								workOrderContactId: _formAC.workOrderContactId,
							});
						}
						return _acc;
					}, [] as ContactRM[]);
					break;

				case 'workLocation': {
					// if location and notes are deleted
					if (!form[_key]?.street && !form[_key]?.notes && !form[_key]?.city && !form[_key]?.location) {
						rm[_key] = null;
						break;
					}

					rm[_key] = form[_key];
					break;
				}

				case 'billToPhoneNumber': {
					if (!!form[_key]) {
						rm[_key] = toRawPhoneNumber(form[_key]!);
					}
					break;
				}

				case 'additionalTruckEquipmentFields': {
					for (let i = 0; i < MAX_WR_TRUCK_EQUIPMENT_ADDITIONAL_FIELDS; i++) {
						rm[`additionalTruckEquipmentField${i + 1}Name`] = form[_key]?.[i]?.name ?? null;
						rm[`additionalTruckEquipmentField${i + 1}Checked`] = form[_key]?.[i]?.isChecked ?? false;
					}
					break;
				}

				default:
					rm[_key] = form[_key];
					break;
			}
		}

		return rm;
	};
}

export default WorkRequestUpsertFM;
